import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, Row, Col, Button, Card, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { ToastContainer, toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AddStudent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const schoolIdFromUrl = searchParams.get('school_id');
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout, data } = useContext(UserContext);
  const headers = {
    "auth-token": authToken,
    "authorization": accessToken
  };

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);

  // Character limit constants
  const CHAR_LIMITS = {
    roll_number: 20,
    first_name: 30,
    last_name: 30,
    father_name: 50,
    email: 120,
    mobile: 14
  };

  const [mobileCharLimit, setMobileCharLimit] = useState(14);
  const [loading, setLoading] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [grades, setGrades] = useState([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [dobDate, setDobDate] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [formData, setFormData] = useState({
    roll_number: "",
    first_name: "",
    last_name: "",
    father_name: "",
    email: "",
    mobile: "",
    country_id: 233,
    grade_id: "",
    dob: "",
    gender: "",
    profile_pic: null,
    school_id: ""
  });

  const [errors, setErrors] = useState({});
  const [existingProfilePic, setExistingProfilePic] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch Grades on component mount
  useEffect(() => {
    fetchSchools();
    fetchCountries();
  }, []);

  // If editing, fetch student details
  useEffect(() => {
    if (id) {
      fetchStudentDetails();
    }
  }, [id]);

  // Fetch grades when school_id changes (for add mode) or when editing
  useEffect(() => {
    if (formData.school_id) {
      fetchGrades(formData.school_id);
    } else {
      setGrades([]);
    }
  }, [formData.school_id]);


  useEffect(() => {
    if (formData.country_id && countries.length > 0) {
      const country = countries.find(c => String(c.id) === String(formData.country_id));
      if (country?.mnl) {
        const mnl = String(country.mnl);
        if (mnl.includes('-')) {
          setMobileCharLimit(parseInt(mnl.split('-').pop(), 10) || 14);
        } else {
          setMobileCharLimit(parseInt(mnl, 10) || 14);
        }
      } else {
        setMobileCharLimit(14);
      }
    }
  }, [formData.country_id, countries]);

  const fetchStudentDetails = async () => {
    setFetchingDetails(true);
    try {
      const response = await APIServices.get(
        `/admin/student/${id}`,
        headers
      );

      if (response?.data?.status === true) {
        const studentData = response?.data?.data;

        // Convert DOB from YYYY-MM-DD to Date object
        let dobDateObj = null;
        if (studentData.dob) {
          dobDateObj = new Date(studentData.dob);
        }

        setFormData({
          roll_number: studentData.roll_number || "",
          first_name: studentData.first_name || "",
          last_name: studentData.last_name || "",
          father_name: studentData.father_name || "",
          email: studentData.email || "",
          mobile: studentData.mobile || "",
          country_id: studentData.country_id || "",
          grade_id: studentData.grade_id || "",
          dob: studentData.dob || "",
          gender: studentData.gender || "",
          profile_pic: null,
          school_id: studentData.school_id || ""
        });

        // Set the date picker state
        setDobDate(dobDateObj);
        setExistingProfilePic(studentData.profile_pic || null);

        // Fetch grades for the student's school in edit mode
        if (studentData.school_id) {
          fetchGrades(studentData.school_id);
        }
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
      showToast("Error fetching student details", "error");
    } finally {
      setFetchingDetails(false);
    }
  };

  const fetchGrades = async (schoolId) => {
    setLoadingGrades(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/grade_list?skip=0&limit=1000&school_id=${schoolId}`,
        headers
      );

      if (response?.data?.status === true) {
        const gradesData = response?.data?.data?.grades || response?.data?.data?.data || [];
        setGrades(gradesData);
      } else {
        setGrades([]);
      }
    } catch (error) {
      console.error("Error fetching grades:", error);
      showToast("Error fetching grades", "error");
      setGrades([]);
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchSchools = async () => {
    setLoadingSchools(true);
    try {
      const response = await APIServices.get(
        `/admin/school/short_list`,
        headers
      );

      if (response?.data?.status === true) {
        const schoolsData = response?.data?.data?.data || [];
        setSchools(schoolsData);

        // For school admin users, auto-select their school
        if (data?.user_type === "school" && schoolsData.length > 0) {
          const userSchool = schoolsData.find(school => school.admin_id === data?.id);
          if (userSchool) {
            setFormData(prev => ({ ...prev, school_id: userSchool.id }));
            fetchGrades(userSchool.id);
          }
        }

        // Auto-select school from URL parameter
        if (schoolIdFromUrl && !id) {
          const schoolFromUrl = schoolsData.find(school => school.id == schoolIdFromUrl);
          if (schoolFromUrl) {
            setFormData(prev => ({ ...prev, school_id: schoolFromUrl.id }));
            fetchGrades(schoolFromUrl.id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
      showToast("Error fetching schools", "error");
    } finally {
      setLoadingSchools(false);
    }
  };

  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await APIServices.get(
        `/admin/cms/country_list`,
        headers
      );

      if (response?.data?.status === true) {
        setCountries(response?.data?.data?.countries || []);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      showToast("Error fetching countries", "error");
    } finally {
      setLoadingCountries(false);
    }
  };
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      const file = files[0];
      setFormData({ ...formData, [name]: file });

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
      setExistingProfilePic(null);
    } else {
      // Apply character limits
      let limitedValue = value;
      if (name === 'mobile') {
        limitedValue = value.slice(0, mobileCharLimit);
      } else if (CHAR_LIMITS[name]) {
        limitedValue = value.slice(0, CHAR_LIMITS[name]);
      }
      setFormData({ ...formData, [name]: limitedValue });

      // Clear grade_id when school changes (for add mode)
      if (name === 'school_id') {
        setFormData(prev => ({ ...prev, grade_id: "" }));
      }
    }

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Add this function after handleChange
  const handleDobChange = (date) => {
    setDobDate(date);
    if (date) {
      // Format as YYYY-MM-DD for storage
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      setFormData({ ...formData, dob: formattedDate });
    } else {
      setFormData({ ...formData, dob: "" });
    }

    if (errors.dob) {
      setErrors({ ...errors, dob: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    const today = new Date().toISOString().split("T")[0];

    if (!formData.roll_number?.trim()) {
      newErrors.roll_number = "Roll number is required";
    } else if (formData.roll_number.length > CHAR_LIMITS.roll_number) {
      newErrors.roll_number = `Roll number cannot exceed ${CHAR_LIMITS.roll_number} characters`;
    }

    if (!formData.first_name?.trim()) {
      newErrors.first_name = "First name is required";
    } else if (formData.first_name.length > CHAR_LIMITS.first_name) {
      newErrors.first_name = `First name cannot exceed ${CHAR_LIMITS.first_name} characters`;
    }

    if (!formData.last_name?.trim()) {
      newErrors.last_name = "Last name is required";
    } else if (formData.last_name.length > CHAR_LIMITS.last_name) {
      newErrors.last_name = `Last name cannot exceed ${CHAR_LIMITS.last_name} characters`;
    }

    if (formData.father_name && formData.father_name.length > CHAR_LIMITS.father_name) {
      newErrors.father_name = `Father's name cannot exceed ${CHAR_LIMITS.father_name} characters`;
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    } else if (formData.email && formData.email.length > CHAR_LIMITS.email) {
      newErrors.email = `Email cannot exceed ${CHAR_LIMITS.email} characters`;
    }

    if (!formData.mobile?.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^\d+$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must contain only digits";
    } else if (formData.mobile.length > mobileCharLimit) {
      newErrors.mobile = `Mobile number cannot exceed ${mobileCharLimit} digits`;
    }

    if (!formData.grade_id) {
      newErrors.grade_id = "Grade is required";
    }
    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
    } else if (formData.dob > today) {
      newErrors.dob = "Date of birth cannot be a future date";
    }
    // Only validate school_id for admin users in add mode
    if (data?.user_type !== "school" && !id && !formData.school_id) {
      newErrors.school_id = "School is required";
    }

    if (!formData.gender) {
      newErrors.gender = "Please select gender";
    }

    if (!formData.country_id) {
      newErrors.country_id = "Please select country code";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      showToast("Please fill all required fields", "error");
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();

      formDataToSend.append("roll_number", formData.roll_number);
      formDataToSend.append("first_name", formData.first_name);
      formDataToSend.append("last_name", formData.last_name);
      formDataToSend.append("father_name", formData.father_name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobile", formData.mobile);
      formDataToSend.append("country_id", formData.country_id); // Add this line
      formDataToSend.append("grade_id", formData.grade_id);
      formDataToSend.append("school_id", formData.school_id);
      formDataToSend.append("dob", formData.dob);
      formDataToSend.append("gender", formData.gender);

      if (formData.profile_pic) {
        formDataToSend.append("profile_pic", formData.profile_pic);
      }

      let response;

      if (id) {
        formDataToSend.append("id", id);
        response = await APIServices.put(
          "/admin/student/update",
          formDataToSend,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );
      } else {
        response = await APIServices.post(
          "/admin/student/add",
          formDataToSend,
          {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        );
      }

      if (response?.data?.status === true) {
        showToast(response?.data?.message || (id ? "Student updated successfully" : "Student added successfully"), "success");
        setTimeout(() => {
          navigate("/admin/student");
        }, 2000);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error("Error saving student:", error);
      showToast(error.response?.data?.message || "An unexpected error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" }
  ];
  const today = new Date().toISOString().split("T")[0];

  if (fetchingDetails) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading student details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="add_student_sec">
        <div className="addmom_header d-flex justify-content-between align-items-center mb-3">
          <div>
            <h1 className="d-inline-block font_blue_18 font-600">
              {id ? "Edit Student" : "Add Student"}
            </h1>
          </div>
        </div>

        <Card className="add_student_form">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {/* School Selection - Conditional based on user type and mode */}
              {data?.user_type !== "school" && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>School <span className="text-danger">*</span></Form.Label>

                      {/* For Update Mode - Disabled and pre-selected */}
                      {id ? (
                        <Form.Control
                          type="text"
                          value={schools.find(s => s.id == formData.school_id)?.school_name || "Loading..."}
                          disabled
                          style={{ backgroundColor: '#e9ecef' }}
                        />
                      ) : (
                        <Form.Select
                          name="school_id"
                          value={formData.school_id}
                          onChange={handleChange}
                          isInvalid={!!errors.school_id}
                          disabled={loadingSchools || loading || (schoolIdFromUrl && !id)}
                        >
                          <option value="">Select School</option>
                          {schools.map((school) => (
                            <option key={school.id} value={school.id}>
                              {school.school_name} ({school.school_code})
                            </option>
                          ))}
                        </Form.Select>
                      )}

                      {loadingSchools && <Spinner animation="border" size="sm" className="ms-2" />}
                      <Form.Control.Feedback type="invalid">
                        {errors.school_id}
                      </Form.Control.Feedback>

                      {/* Show message when school is auto-selected from URL */}
                      {schoolIdFromUrl && !id && (
                        <Form.Text className="text-info">
                          School has been auto-selected from the link
                        </Form.Text>
                      )}

                      {/* Show message for update mode */}
                      {id && (
                        <Form.Text className="text-muted">
                          School cannot be changed after student creation
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* For school admin users - Show info message with pre-selected school */}
              {data?.user_type === "school" && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>School</Form.Label>
                      <Form.Control
                        type="text"
                        value={schools.find(s => s.id == formData.school_id)?.school_name || "Your School"}
                        disabled
                        style={{ backgroundColor: '#e9ecef' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Roll Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="roll_number"
                      placeholder="Enter roll number"
                      value={formData.roll_number}
                      onChange={handleChange}
                      isInvalid={!!errors.roll_number}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.roll_number}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.roll_number}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      placeholder="Enter first name"
                      value={formData.first_name}
                      onChange={handleChange}
                      isInvalid={!!errors.first_name}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.first_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.first_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      placeholder="Enter last name"
                      value={formData.last_name}
                      onChange={handleChange}
                      isInvalid={!!errors.last_name}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.last_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.last_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Father's Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="father_name"
                      placeholder="Enter father's name"
                      value={formData.father_name}
                      onChange={handleChange}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.father_name}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="Enter email"
                      value={formData.email}
                      onChange={handleChange}
                      isInvalid={!!errors.email}
                      disabled={loading}
                      maxLength={CHAR_LIMITS.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="font_blue_14 font-500 mb-2">Mobile Number <span className="text-danger">*</span></Form.Label>
                    <div className="d-flex" style={{ border: "1px solid #ced4da", borderRadius: "4px", overflow: "hidden" }}>
                      <Form.Select
                        name="country_id"
                        value={formData.country_id}
                        onChange={handleChange}
                        disabled={loading || loadingCountries}
                        isInvalid={!!errors.country_id}
                        style={{
                          width: "180px",
                          border: "none",
                          backgroundColor: "#f8f9fa",
                          borderRight: "2px solid #ced4da",
                          borderRadius: "4px 0 0 4px"
                        }}
                      >
                        <option value="">Select Code</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name} ({country.phonecode})
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control
                        type="text"
                        name="mobile"
                        placeholder="Enter mobile number"
                        value={formData.mobile}
                        onChange={handleChange}
                        isInvalid={!!errors.mobile}
                        disabled={loading}
                        maxLength={mobileCharLimit}
                        style={{
                          border: "none",
                          flex: 1,
                          borderRadius: "0 4px 4px 0"
                        }}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    {errors.country_id && (
                      <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                        {errors.country_id}
                      </div>
                    )}
                    {errors.mobile && (
                      <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                        {errors.mobile}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Grade <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="grade_id"
                      value={formData.grade_id}
                      onChange={handleChange}
                      isInvalid={!!errors.grade_id}
                      disabled={!formData.school_id || loadingGrades || loading}
                    >
                      <option value="">
                        {!formData.school_id
                          ? "Please select school first"
                          : loadingGrades
                            ? "Loading grades..."
                            : "Select Grade"}
                      </option>
                      {grades.map((grade) => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name}
                        </option>
                      ))}
                    </Form.Select>
                    {loadingGrades && <Spinner animation="border" size="sm" className="ms-2" />}
                    <Form.Control.Feedback type="invalid">
                      {errors.grade_id}
                    </Form.Control.Feedback>
                    {!formData.school_id && !id && (
                      <Form.Text className="text-warning">
                        Please select a school first to load grades
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3 expiry-date-picker">
                    <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                    <br></br>
                    <DatePicker
                      selected={dobDate}
                      onChange={handleDobChange}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="mm/dd/yyyy"
                      className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                      maxDate={new Date()} // Cannot select future dates
                      disabled={loading}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                    />
                    {errors.dob && (
                      <div className="invalid-feedback d-block">
                        {errors.dob}
                      </div>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gender <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      isInvalid={!!errors.gender}
                      disabled={loading}
                    >
                      <option value="">Select Gender</option>
                      {genderOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.gender}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Profile Picture</Form.Label>
                    <Form.Control
                      type="file"
                      name="profile_pic"
                      accept="image/*"
                      onChange={handleChange}
                      disabled={loading}
                    />

                    {/* Image Preview */}
                    {(existingProfilePic || imagePreview) && (
                      <div className="mt-2">
                        {existingProfilePic && !imagePreview ? (
                          <div>
                            <img
                              src={existingProfilePic}
                              alt="Current Profile"
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                border: '1px solid #ddd',
                                padding: '3px'
                              }}
                            />
                          </div>
                        ) : imagePreview && (
                          <div>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                border: '2px solid #28a745',
                                padding: '3px'
                              }}
                            />
                            <div className="mt-1">
                              <small className="text-success">New Profile Preview</small>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Submit Buttons */}
              <div className="text-center mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="me-2"
                >
                  {loading ? "Processing..." : (id ? "Update" : "Add")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate("/admin/student")}
                  disabled={loading}
                >
                  Back
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <ToastContainer />
    </>
  );
};

export default AddStudent;